import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../services/store';

import { PURGE } from 'redux-persist';

import { SearchState } from '../types/library';
import { selectPublicById } from '../services/search';
import { SortDirection, SortType } from '../types/common';

const initialState: SearchState = {
  page: 0,
  options: {
    sortType: SortType.REG_DATE,
    sortOrder: SortDirection.DESC,
    layoutType: 'grid',
    isEditMode: false,
    isDetailsOpened: false,
  },
  selectedIds: [],
  selectedItem: null,
  searchWord: '',
  searchInpVal: '',
};

export const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    searchPageChanged: (state, action) => {
      state.page = action.payload;
    },
    searchSortTypeChanged: (state, action) => {
      state.options.sortType = action.payload;
    },
    searchOrderChanged: (
      state,
      action: PayloadAction<SortDirection, string>
    ) => {
      state.options.sortOrder = action.payload;
    },
    searchLayoutTypeChanged: (state, action) => {
      state.options.layoutType = action.payload;
    },
    searchEditModeToggled: (state, action) => {
      state.options.isEditMode = action.payload;
    },
    searchDetailsToggled: (
      state,
      action: PayloadAction<boolean | undefined, string>
    ) => {
      state.options.isDetailsOpened =
        action.payload ?? !state.options.isDetailsOpened;
    },
    searchItemSelected: (state, action) => {
      state.selectedIds = action.payload.ids;
      if (action.payload.item !== undefined) {
        state.selectedItem = action.payload.item;
      }
    },
    searchIndexListChanged: (state, action) => {
      if (state.selectedItem) {
        state.selectedItem.projectIndex.indexList = action.payload;
      }
    },
    searchIndexIndentChanged: (state, action) => {
      const item = state.selectedItem?.projectIndex.indexList[action.payload];
      if (item) {
        item.indentYn = item.indentYn ? 0 : 1;
      }
    },
    searchWordSet: (state, action) => {
      state.searchWord = action.payload;
    },
    searchInpValSet: (state, action) => {
      state.searchInpVal = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState);
  },
});

export const {
  searchPageChanged,
  searchSortTypeChanged,
  searchOrderChanged,
  searchLayoutTypeChanged,
  searchEditModeToggled,
  searchDetailsToggled,
  searchItemSelected,
  searchIndexListChanged,
  searchIndexIndentChanged,
  searchWordSet,
  searchInpValSet,
} = searchSlice.actions;

export const selectSearchPage = (state: RootState) => state.search.page;

export const selectSearchOptions = (state: RootState) => state.search.options;

export const selectSearchSelectedIds = (state: RootState) =>
  state.search.selectedIds;

export const selectSearchSelectedId = createSelector(
  selectSearchSelectedIds,
  (selectedIds) => selectedIds[0]
);

export const selectSearchSelectedItems = (state: RootState) =>
  state.search.selectedIds.map((id) => selectPublicById(state, id));

export const selectSearchSingleItem = createSelector(
  selectSearchSelectedItems,
  (selectedItems) => selectedItems[0]
);

export const selectSearchSelectedItem = (state: RootState) =>
  state.search.selectedItem;

export const selectSearchProject = createSelector(
  selectSearchSelectedItem,
  (selectedItem) => selectedItem?.project
);

export const selectProjectPublic = createSelector(
  selectSearchSelectedItem,
  (selectedItem) => selectedItem?.projectPublic
);

export const selectProjectHistoryList = createSelector(
  selectSearchSelectedItem,
  (selectedItem) => selectedItem?.projectHistoryList
);

export const selectSearchWord = (state: RootState) => state.search.searchWord;
export const selectSearchInpVal = (state: RootState) =>
  state.search.searchInpVal;

export default searchSlice.reducer;
